import { useEffect, useMemo, useRef, useState } from 'react';
import type { AnimationEvent, CSSProperties } from 'react';
import { Background, BackgroundVariant, BaseEdge, Handle, Position, ReactFlow } from '@xyflow/react';
import type { EdgeProps, ReactFlowInstance } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './SubsystemDiagramModal.scss';
import { getSubsystemIcon } from '../../Missions/Satellites/TestCase/svgs/TestCaseSvgs';
import type { ValidateSubsystemHealthStatus } from '../types/validateDetails.types';
import { normalizeSubsystemHealthStatus } from '../helpers/validateDetails.helpers';

export type SubsystemDiagramItem = {
  name: string;
  healthStatus?: ValidateSubsystemHealthStatus | string;
  failedTestCases?: string[];
};

type SubsystemDiagramMode = 'setup' | 'session';
type DiagramStatus = 'root' | 'healthy' | 'unhealthy' | 'pending' | 'unknown';
type DiagramHandlePosition = 'left' | 'right' | 'top' | 'bottom';
type DiagramHandleSpec = {
  id: string;
  position: DiagramHandlePosition;
  offsetPercent?: number;
};

type SubsystemDiagramModalProps = {
  open: boolean;
  title: string;
  rootLabel: string;
  sessionLabel?: string;
  setupLabel?: string;
  mode: SubsystemDiagramMode;
  subsystems: SubsystemDiagramItem[];
  loading?: boolean;
  sanityExecutionStatus?: string;
  sanityTestPlanId?: string;
  updatedAt?: number | string | Date | null;
  runSanityLoading?: boolean;
  onRunSanityTest?: (sanityTestPlanId: string) => void | Promise<void>;
  isSessionCompleted?: boolean;
  onClose: () => void;
};

type DiagramNodeData = {
  label: string;
  status: DiagramStatus;
  badge: string;
  failedTestCases?: string[];
  selectedFailure?: boolean;
  rootNode?: boolean;
  hubNode?: boolean;
  sourceHandle?: DiagramHandlePosition;
  targetHandle?: DiagramHandlePosition;
  sourceHandles?: DiagramHandlePosition[];
  targetHandles?: DiagramHandlePosition[];
  sourceHandleSpecs?: DiagramHandleSpec[];
  targetHandleSpecs?: DiagramHandleSpec[];
  staticNode?: boolean;
};

type DiagramEdgeData = {
  routeOffset?: number;
  routeDistance?: number;
  routeAnchor?: 'source' | 'target';
};

type DiagramFlowNode = {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: DiagramNodeData;
};

type DiagramFlowEdge = {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  type: string;
  className: string;
  data?: DiagramEdgeData;
};

const SESSION_TREE_CENTER_X = 356;
const SESSION_SETUP_Y = 48;
const SESSION_NODE_Y = 168;
const SESSION_SUBSYSTEM_Y = 318;
const SESSION_SUBSYSTEM_COLUMN_GAP = 280;
const SESSION_SUBSYSTEM_ROW_GAP = 110;
const SETUP_MODE_ROOT_Y = 92;
const SETUP_MODE_SUBSYSTEM_Y = 258;
const DIAGRAM_NODE_WIDTH = 226;
const ROOT_NODE_HEIGHT = 58;
const HUB_NODE_HEIGHT = 96;
const SUBSYSTEM_NODE_HEIGHT = 58;
const SUBSYSTEM_NODE_FAILURE_HEIGHT = 94;
const TOPOLOGY_LEFT_X = 80;
const TOPOLOGY_HUB_X = 520;
const TOPOLOGY_RIGHT_X = 900;
const TOPOLOGY_PAYLOAD_X = 1220;
const TOPOLOGY_LEFT_START_Y = 110;
const TOPOLOGY_RIGHT_START_Y = 110;
const TOPOLOGY_ROW_GAP = 110;
const TOPOLOGY_HUB_Y = 300;
const TOPOLOGY_BOTTOM_Y = 530;
const TOPOLOGY_GROUND_Y = 660;
const DRAWER_VIEWPORT_WIDTH = 328;
const VIEWPORT_SIDE_GUTTER = 44;
const VIEWPORT_VERTICAL_GUTTER = 42;

function normalizeStatus(status: SubsystemDiagramItem['healthStatus']): DiagramStatus {
  return normalizeSubsystemHealthStatus(status).toLowerCase() as DiagramStatus;
}

function statusBadge(status: DiagramStatus, mode: SubsystemDiagramMode): string {
  if (mode === 'setup') return '';
  if (status === 'healthy') return 'HEALTHY';
  if (status === 'unhealthy') return 'UNHEALTHY';
  if (status === 'pending') return 'PENDING';
  return 'UNKNOWN';
}

function sanityStatusCopy(status?: string): string {
  const normalizedStatus = status?.trim().toUpperCase();
  if (!normalizedStatus) return 'Sanity Pending';
  if (['PASSED', 'PASS', 'SUCCESS', 'SUCCEEDED', 'COMPLETED'].includes(normalizedStatus)) return 'Sanity Passed';
  if (['FAILED', 'FAIL', 'ERROR'].includes(normalizedStatus)) return 'Sanity Failed';
  if (['RUNNING', 'IN_PROGRESS', 'INPROGRESS'].includes(normalizedStatus)) return 'Sanity Running';
  return 'Sanity Pending';
}

function sanityStatusTone(status?: string): DiagramStatus {
  const normalizedStatus = status?.trim().toUpperCase();
  if (['PASSED', 'PASS', 'SUCCESS', 'SUCCEEDED', 'COMPLETED'].includes(normalizedStatus ?? '')) return 'healthy';
  if (['FAILED', 'FAIL', 'ERROR'].includes(normalizedStatus ?? '')) return 'unhealthy';
  if (['RUNNING', 'IN_PROGRESS', 'INPROGRESS', 'PENDING'].includes(normalizedStatus ?? '')) return 'pending';
  return 'unknown';
}

function updatedAtTimeMs(updatedAt?: number | string | Date | null): number | null {
  if (updatedAt == null) return null;
  const timeMs = updatedAt instanceof Date ? updatedAt.getTime() : new Date(updatedAt).getTime();
  return Number.isFinite(timeMs) ? timeMs : null;
}

function updatedAtCopy(updatedAt?: number | string | Date | null, nowMs = Date.now()): string {
  const timeMs = updatedAtTimeMs(updatedAt);
  if (timeMs == null) return '';

  const diffSeconds = Math.max(0, Math.floor((nowMs - timeMs) / 1000));
  if (diffSeconds < 10) return 'Updated just now';
  if (diffSeconds < 60) return `Updated ${diffSeconds} secs ago`;

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `Updated ${diffMinutes} mins ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Updated ${diffHours} hrs ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `Updated ${diffDays} days ago`;
}

function buildSummary(mode: SubsystemDiagramMode, subsystems: SubsystemDiagramItem[]) {
  return subsystems.reduce(
    (summary, subsystem) => {
      const status = mode === 'setup' ? 'unknown' : normalizeStatus(subsystem.healthStatus);
      summary[status] += 1;
      return summary;
    },
    { root: 0, healthy: 0, unhealthy: 0, pending: 0, unknown: 0 } as Record<DiagramStatus, number>,
  );
}

function testCaseCopy(failedTestCases: string[]): { countLabel: string; detailLabel: string; title: string } {
  const uniqueFailedTestCases = getUniqueFailedTestCases(failedTestCases) ?? [];
  const count = uniqueFailedTestCases.length;
  const tooltipPrefix = count === 1 ? 'Failed test case' : 'Failed test cases';
  const formattedTestCases = uniqueFailedTestCases.map(formatTestCaseName);
  const readableTestCases = uniqueFailedTestCases.map(formatReadableTestCaseName);
  const firstReadableTestCase = readableTestCases[0] || 'Unknown';
  return {
    countLabel: `${count} Failed`,
    detailLabel: count > 1 ? `${firstReadableTestCase} +${count - 1}` : firstReadableTestCase,
    title: `${tooltipPrefix}: ${formattedTestCases.join(', ')}`,
  };
}

function getUniqueFailedTestCases(failedTestCases?: string[]): string[] | undefined {
  if (!failedTestCases?.length) return undefined;

  const seen = new Set<string>();
  const uniqueFailedTestCases = failedTestCases.reduce<string[]>((acc, testCase) => {
    const value = String(testCase ?? '').trim();
    const key = value.toUpperCase();
    if (!value || seen.has(key)) return acc;
    seen.add(key);
    acc.push(value);
    return acc;
  }, []);

  return uniqueFailedTestCases.length ? uniqueFailedTestCases : undefined;
}

function failedTestCaseDetailCopy(count: number) {
  const isSingleTestCase = count === 1;

  return {
    drawerLabel: `Linked failed test ${isSingleTestCase ? 'case' : 'cases'}`,
    closeLabel: 'Collapse subsystem health detail',
    sectionTitle: `Failed Test ${isSingleTestCase ? 'Case' : 'Cases'}`,
    hint: `${isSingleTestCase ? 'This test case is' : 'These test cases are'} linked to the subsystem health reported by setup sanity.`,
  };
}

function formatTestCaseName(testCase: string): string {
  const value = testCase.trim();
  if (!value) return testCase;
  const readable = formatReadableTestCaseName(value);

  return readable && readable !== value ? `${readable} (${value})` : value;
}

function formatReadableTestCaseName(testCase: string): string {
  const value = testCase.trim();
  if (!value) return testCase;
  const readable = value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => (part.length <= 3 ? part.toUpperCase() : `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`))
    .join(' ');

  return readable || value;
}

function getViewportSettings(subsystemCount: number) {
  if (subsystemCount <= 3) {
    return { minZoom: 0.50, maxZoom: 1.05, padding: 0.16 };
  }
  if (subsystemCount <= 6) {
    return { minZoom: 0.42, maxZoom: 0.90, padding: 0.15 };
  }
  return { minZoom: 0.34, maxZoom: 0.78, padding: 0.14 };
}

function getNodeHeight(node: DiagramFlowNode): number {
  if (node.data.rootNode) return ROOT_NODE_HEIGHT;
  if (node.data.hubNode) return HUB_NODE_HEIGHT;
  return node.data.failedTestCases?.length ? SUBSYSTEM_NODE_FAILURE_HEIGHT : SUBSYSTEM_NODE_HEIGHT;
}

function handlePosition(handle: DiagramHandlePosition): Position {
  if (handle === 'left') return Position.Left;
  if (handle === 'top') return Position.Top;
  if (handle === 'bottom') return Position.Bottom;
  return Position.Right;
}

function uniqueHandles(...handleGroups: Array<DiagramHandlePosition | DiagramHandlePosition[] | undefined>) {
  const seen = new Set<DiagramHandlePosition>();
  return handleGroups.flatMap((handleGroup) => {
    const handles = Array.isArray(handleGroup)
      ? handleGroup
      : handleGroup
        ? [handleGroup]
        : [];

    return handles.filter((handle) => {
      if (seen.has(handle)) return false;
      seen.add(handle);
      return true;
    });
  });
}

function toHandleSpecs(
  singleHandle: DiagramHandlePosition | undefined,
  handles: DiagramHandlePosition[] | undefined,
  specs: DiagramHandleSpec[] | undefined,
): DiagramHandleSpec[] {
  const seen = new Set<string>();
  const simpleSpecs = uniqueHandles(singleHandle, handles).map((handle) => ({
    id: handle,
    position: handle,
  }));

  return [...simpleSpecs, ...(specs ?? [])].filter((spec) => {
    if (seen.has(spec.id)) return false;
    seen.add(spec.id);
    return true;
  });
}

function getHandleStyle(spec: DiagramHandleSpec): CSSProperties | undefined {
  if (spec.offsetPercent == null) return undefined;
  if (spec.position === 'left' || spec.position === 'right') {
    return { top: `${spec.offsetPercent}%` };
  }
  return { left: `${spec.offsetPercent}%` };
}

function spreadHandleOffset(count: number, index: number, min = 20, max = 80): number {
  if (count <= 1) return 50;
  return min + ((max - min) * index) / (count - 1);
}

function spreadRouteOffset(count: number, index: number, range = 56): number {
  if (count <= 1) return 0;
  const scaledRange = range + Math.max(0, (count - 3) * 16);
  return -scaledRange + ((scaledRange * 2) * index) / (count - 1);
}

function routeLaneDistance(count: number, index: number, min = 48, max = 150): number {
  if (count <= 1) return min;
  const scaledMax = max + Math.max(0, (count - 4) * 20);
  return min + ((scaledMax - min) * index) / (count - 1);
}

function computeRoutingIndices(
  count: number,
  getHandleY: (index: number) => number,
  getNodeCenterY: (index: number) => number,
): number[] {
  if (count <= 1) return count === 0 ? [] : [0];
  const distances = Array.from({ length: count }, (_, i) =>
    Math.abs(getHandleY(i) - getNodeCenterY(i))
  );
  // Largest vertical distance → smallest routing index → smallest elbow (prevents crossings)
  const order = Array.from({ length: count }, (_, i) => i).sort((a, b) => distances[b] - distances[a]);
  const result = new Array<number>(count);
  order.forEach((originalIdx, rank) => { result[originalIdx] = rank; });
  return result;
}

type DiagramPoint = { x: number; y: number };

function getRoundedStepPath(points: DiagramPoint[], radius = 4): string {
  if (points.length < 2) return '';

  const commandParts = [`M ${points[0].x} ${points[0].y}`];

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1];
    const previousDistance = Math.abs(current.x - previous.x) + Math.abs(current.y - previous.y);
    const nextDistance = Math.abs(next.x - current.x) + Math.abs(next.y - current.y);
    const cornerRadius = Math.min(radius, previousDistance / 4, nextDistance / 4);
    const isCollinear = (previous.x === current.x && current.x === next.x)
      || (previous.y === current.y && current.y === next.y);

    if (cornerRadius <= 0 || isCollinear) {
      commandParts.push(`L ${current.x} ${current.y}`);
      continue;
    }

    const beforeCorner = previous.x === current.x
      ? { x: current.x, y: current.y + Math.sign(previous.y - current.y) * cornerRadius }
      : { x: current.x + Math.sign(previous.x - current.x) * cornerRadius, y: current.y };
    const afterCorner = next.x === current.x
      ? { x: current.x, y: current.y + Math.sign(next.y - current.y) * cornerRadius }
      : { x: current.x + Math.sign(next.x - current.x) * cornerRadius, y: current.y };

    commandParts.push(
      `L ${beforeCorner.x} ${beforeCorner.y}`,
      `A ${cornerRadius} ${cornerRadius} 0 0 1 ${afterCorner.x} ${afterCorner.y}`,
    );
  }

  const lastPoint = points[points.length - 1];
  commandParts.push(`L ${lastPoint.x} ${lastPoint.y}`);
  return commandParts.join(' ');
}

function TopologyEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}: EdgeProps & { data?: DiagramEdgeData }) {
  const isVerticalRoute = sourcePosition === Position.Bottom
    || sourcePosition === Position.Top
    || targetPosition === Position.Bottom
    || targetPosition === Position.Top;

  const routeOffset = typeof data?.routeOffset === 'number' ? data.routeOffset : 0;
  const routeAnchor = data?.routeAnchor ?? 'source';
  const routeDistance = typeof data?.routeDistance === 'number' ? data.routeDistance : null;
  const horizontalDirection = sourceX <= targetX ? 1 : -1;
  const verticalDirection = sourceY <= targetY ? 1 : -1;
  const routeGap = routeDistance ?? Math.max(48, 72 + Math.abs(routeOffset) * 0.64);

  let points: DiagramPoint[];

  if (isVerticalRoute) {
    const verticalElbowY = routeAnchor === 'target'
      ? targetY - verticalDirection * routeGap
      : sourceY + verticalDirection * routeGap;
    points = [
      { x: sourceX, y: sourceY },
      { x: sourceX, y: verticalElbowY },
      { x: targetX, y: verticalElbowY },
      { x: targetX, y: targetY },
    ];
  } else {
    const horizontalElbowX = routeAnchor === 'target'
      ? targetX - horizontalDirection * routeGap
      : sourceX + horizontalDirection * routeGap;
    points = [
      { x: sourceX, y: sourceY },
      { x: horizontalElbowX, y: sourceY },
      { x: horizontalElbowX, y: targetY },
      { x: targetX, y: targetY },
    ];
  }

  return (
    <BaseEdge
      id={id}
      path={getRoundedStepPath(points)}
      markerEnd={markerEnd}
      style={style}
    />
  );
}

function SetupChipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M12.6667 2H3.33333C2.59695 2 2 2.59695 2 3.33333V12.6667C2 13.403 2.59695 14 3.33333 14H12.6667C13.403 14 14 13.403 14 12.6667V3.33333C14 2.59695 13.403 2 12.6667 2Z" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.33331 6H9.99998C10.3536 6 10.6927 5.85952 10.9428 5.60948C11.1928 5.35943 11.3333 5.02029 11.3333 4.66667V2" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.00002 7.33464C6.7364 7.33464 7.33335 6.73768 7.33335 6.0013C7.33335 5.26492 6.7364 4.66797 6.00002 4.66797C5.26364 4.66797 4.66669 5.26492 4.66669 6.0013C4.66669 6.73768 5.26364 7.33464 6.00002 7.33464Z" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.66669 14V11.3333C4.66669 10.9797 4.80716 10.6406 5.05721 10.3905C5.30726 10.1405 5.6464 10 6.00002 10H8.66669" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 11.3346C10.7364 11.3346 11.3334 10.7377 11.3334 10.0013C11.3334 9.26492 10.7364 8.66797 10 8.66797C9.26364 8.66797 8.66669 9.26492 8.66669 10.0013C8.66669 10.7377 9.26364 11.3346 10 11.3346Z" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function getDiagramBounds(nodes: DiagramFlowNode[]) {
  if (nodes.length === 0) return null;

  const bounds = nodes.reduce(
    (acc, node) => {
      const nodeHeight = getNodeHeight(node);
      return {
        minX: Math.min(acc.minX, node.position.x),
        minY: Math.min(acc.minY, node.position.y),
        maxX: Math.max(acc.maxX, node.position.x + DIAGRAM_NODE_WIDTH),
        maxY: Math.max(acc.maxY, node.position.y + nodeHeight),
      };
    },
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
  );

  return {
    x: bounds.minX,
    y: bounds.minY,
    width: Math.max(bounds.maxX - bounds.minX, DIAGRAM_NODE_WIDTH),
    height: Math.max(bounds.maxY - bounds.minY, ROOT_NODE_HEIGHT),
  };
}

function fitDiagramViewport(
  flowInstance: ReactFlowInstance<DiagramFlowNode, DiagramFlowEdge> | null,
  canvasElement: HTMLDivElement | null,
  nodes: DiagramFlowNode[],
  viewportSettings: ReturnType<typeof getViewportSettings>,
  drawerOpen: boolean,
  useCloserView = false,
) {
  if (!flowInstance || !canvasElement || nodes.length === 0) return;

  const canvasRect = canvasElement.getBoundingClientRect();
  const bounds = getDiagramBounds(nodes);

  if (!bounds || canvasRect.width === 0 || canvasRect.height === 0) {
    flowInstance.fitView({
      padding: viewportSettings.padding,
      minZoom: viewportSettings.minZoom,
      maxZoom: viewportSettings.maxZoom,
      duration: 220,
    });
    return;
  }

  const sideGutter = useCloserView ? 28 : VIEWPORT_SIDE_GUTTER;
  const verticalGutter = useCloserView ? 30 : VIEWPORT_VERTICAL_GUTTER;
  const maxZoom = useCloserView ? Math.min(viewportSettings.maxZoom + 0.08, 1.05) : viewportSettings.maxZoom;
  const availableWidth = Math.max(
    canvasRect.width - (drawerOpen ? DRAWER_VIEWPORT_WIDTH : 0) - sideGutter * 2,
    260,
  );
  const availableHeight = Math.max(canvasRect.height - verticalGutter * 2, 220);
  const naturalZoom = Math.min(availableWidth / bounds.width, availableHeight / bounds.height);
  const zoom = Math.min(maxZoom, Math.max(viewportSettings.minZoom, naturalZoom));
  const viewportCenterX = sideGutter + availableWidth / 2;
  const viewportCenterY = verticalGutter + availableHeight / 2;

  flowInstance.setViewport(
    {
      x: viewportCenterX - (bounds.x + bounds.width / 2) * zoom,
      y: viewportCenterY - (bounds.y + bounds.height / 2) * zoom,
      zoom,
    },
    { duration: drawerOpen ? 260 : 220 },
  );
}

function DiagramNode({ data }: { data: DiagramNodeData }) {
  const showFooter = data.rootNode || (!data.staticNode && !data.rootNode);
  const failedTestCaseInfo = data.failedTestCases?.length
    ? testCaseCopy(data.failedTestCases)
    : null;
  const nodeClassName = [
    'sdm__node',
    `sdm__node--${data.status}`,
    data.rootNode ? 'sdm__node--root' : '',
    data.hubNode ? 'sdm__node--hub' : '',
    failedTestCaseInfo ? 'sdm__node--has-failure' : '',
    data.selectedFailure ? 'sdm__node--selected' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={nodeClassName}>
      <div className="sdm__node__header">
        <span className="sdm__node__dot" />
        {!data.rootNode && <span className="sdm__node__icon">{getSubsystemIcon(data.label)}</span>}
        <span className="sdm__node__label" title={data.label}>{data.label}</span>
      </div>
      {showFooter && (
        <div className="sdm__node__footer">
          <span className="sdm__node__badge">{data.badge}</span>
          {failedTestCaseInfo && (
            <span className="sdm__node__failed-count" title={failedTestCaseInfo.title}>
              {failedTestCaseInfo.countLabel}
            </span>
          )}
        </div>
      )}
      {failedTestCaseInfo && (
        <div className="sdm__node__failure" title={failedTestCaseInfo.title}>
          <span className="sdm__node__failure-label">
            {(getUniqueFailedTestCases(data.failedTestCases)?.length ?? 0) === 1 ? 'Test Case' : 'Test Cases'}
          </span>
          <span className="sdm__node__failure-value">{failedTestCaseInfo.detailLabel}</span>
        </div>
      )}
      {toHandleSpecs(data.sourceHandle, data.sourceHandles, data.sourceHandleSpecs).map((sourceHandle) => (
        <Handle
          key={`source-${sourceHandle.id}`}
          id={sourceHandle.id}
          type="source"
          position={handlePosition(sourceHandle.position)}
          style={getHandleStyle(sourceHandle)}
          className="sdm__node__handle"
        />
      ))}
      {toHandleSpecs(data.targetHandle, data.targetHandles, data.targetHandleSpecs).map((targetHandle) => (
        <Handle
          key={`target-${targetHandle.id}`}
          id={targetHandle.id}
          type="target"
          position={handlePosition(targetHandle.position)}
          style={getHandleStyle(targetHandle)}
          className="sdm__node__handle"
        />
      ))}
    </div>
  );
}

type TopologyLane = 'hub' | 'left' | 'right' | 'payload' | 'bottom' | 'bottom-left' | 'bottom-right' | 'ground' | 'unknown';
type TopologyEntry = {
  subsystem: SubsystemDiagramItem;
  index: number;
  lane: TopologyLane;
  order: number;
};

function normalizedSubsystemName(name: string): string {
  return name.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function classifySubsystem(name: string): { lane: TopologyLane; order: number } {
  const normalized = normalizedSubsystemName(name);

  if (
    normalized === 'OBC'
    || normalized.includes('ONBOARDCONTROLLER')
    || normalized.includes('ONBOARDCOMPUTER')
    || normalized.includes('BUSOBC')
  ) {
    return { lane: 'hub', order: 0 };
  }
  if (normalized.includes('EPS')) return { lane: 'left', order: 0 };
  if (normalized.includes('ADCS') || normalized.includes('IADCS')) return { lane: 'left', order: 1 };
  if (normalized.includes('GPS') || normalized.includes('GNSS')) return { lane: 'left', order: 2 };
  if (normalized.includes('THRUSTER') || normalized.includes('PROPULSION')) return { lane: 'left', order: 3 };
  if (normalized.includes('PAYLOADSERVER') || normalized.includes('PAYLOADOBC')) return { lane: 'right', order: 0 };
  if (normalized.includes('EDGE')) return { lane: 'right', order: 1 };
  if (normalized.includes('IDRSS')) return { lane: 'right', order: 2 };
  if (normalized.includes('XBAND')) return { lane: 'right', order: 3 };
  if (normalized.includes('SBAND')) return { lane: 'bottom-left', order: 0 };
  if (normalized.includes('UHF')) return { lane: 'bottom-right', order: 0 };
  if (normalized.includes('GSSDR') || normalized.includes('GROUNDSDR')) return { lane: 'ground', order: 0 };
  if (
    normalized.includes('AIS')
    || normalized.includes('IMAGING')
    || normalized.includes('CAMERA')
    || normalized.includes('RADAR')
    || normalized.includes('PAYLOAD')
  ) {
    return { lane: 'payload', order: normalized.includes('IMAGING') ? 0 : 1 };
  }

  return { lane: 'unknown', order: 10 };
}

function getDisplayLabel(name: string): string {
  return normalizedSubsystemName(name) === 'OBC' ? 'On-Board Controller' : name;
}

function getTopologyStatus(mode: SubsystemDiagramMode, subsystem?: SubsystemDiagramItem): DiagramStatus {
  if (!subsystem) return 'root';
  return mode === 'setup' ? 'unknown' : normalizeStatus(subsystem.healthStatus);
}

function edgeClassName(status: DiagramStatus, mode: SubsystemDiagramMode, isSelectedFailure = false) {
  return [
    'sdm__edge',
    'sdm__edge--topology',
    mode === 'session' ? 'sdm__edge--health-link' : 'sdm__edge--setup-link',
    mode === 'session' ? `sdm__edge--${status}` : '',
    isSelectedFailure ? 'sdm__edge--selected' : '',
  ].filter(Boolean).join(' ');
}

function makeSubsystemNode(
  entry: TopologyEntry,
  mode: SubsystemDiagramMode,
  position: { x: number; y: number },
  handles: Pick<DiagramNodeData, 'sourceHandles' | 'targetHandles' | 'sourceHandle' | 'targetHandle' | 'sourceHandleSpecs' | 'targetHandleSpecs'>,
  selectedFailureNodeId?: string | null,
): DiagramFlowNode {
  const status = getTopologyStatus(mode, entry.subsystem);
  const nodeId = `subsystem-${entry.index}`;
  const failedTestCases = getUniqueFailedTestCases(entry.subsystem.failedTestCases);

  return {
    id: nodeId,
    type: 'subsystemDiagram',
    position,
    data: {
      label: getDisplayLabel(entry.subsystem.name),
      status,
      badge: statusBadge(status, mode),
      failedTestCases,
      selectedFailure: nodeId === selectedFailureNodeId,
      staticNode: mode === 'setup',
      ...handles,
    },
  };
}

function addTopologyEdge(
  edges: DiagramFlowEdge[],
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
  status: DiagramStatus,
  mode: SubsystemDiagramMode,
  selectedFailureNodeId?: string | null,
  routeOffset = 0,
  routeAnchor: DiagramEdgeData['routeAnchor'] = 'source',
  routeDistance?: number,
) {
  edges.push({
    id: `${source}-${sourceHandle}-${target}-${targetHandle}`,
    source,
    sourceHandle,
    target,
    targetHandle,
    type: 'topology',
    className: edgeClassName(status, mode, source === selectedFailureNodeId || target === selectedFailureNodeId),
    data: { routeOffset, routeAnchor, routeDistance },
  });
}

function buildTopologyDiagram(
  mode: SubsystemDiagramMode,
  subsystems: SubsystemDiagramItem[],
  selectedFailureNodeId?: string | null,
) {
  const sortedEntries = subsystems
    .map((subsystem, index): TopologyEntry => {
      const classification = classifySubsystem(subsystem.name);
      return { subsystem, index, ...classification };
    })
    .sort((a, b) => a.order - b.order || a.index - b.index);

  const nodes: DiagramFlowNode[] = [];
  const edges: DiagramFlowEdge[] = [];
  const entriesByLane = sortedEntries.reduce<Record<TopologyLane, TopologyEntry[]>>((acc, entry) => {
    acc[entry.lane].push(entry);
    return acc;
  }, {
    hub: [],
    left: [],
    right: [],
    payload: [],
    bottom: [],
    'bottom-left': [],
    'bottom-right': [],
    ground: [],
    unknown: [],
  });

  const hubEntry = entriesByLane.hub[0];
  const hubId = hubEntry ? `subsystem-${hubEntry.index}` : 'virtual-obc';
  const leftEntries = entriesByLane.left;
  const extraHubEntries = entriesByLane.hub.slice(1);
  const rightEntries = [...entriesByLane.right, ...entriesByLane.unknown, ...extraHubEntries];
  const payloadEntries = entriesByLane.payload;
  const bottomEntries = entriesByLane.bottom;
  const bottomLeftEntries = entriesByLane['bottom-left'];
  const bottomRightEntries = entriesByLane['bottom-right'];
  const groundEntries = entriesByLane.ground;
  const payloadParentEntry = entriesByLane.right.find((entry) => entry.order === 0);
  const directPayloadEntries = payloadParentEntry ? [] : payloadEntries;
  const directGroundEntries = bottomEntries.length || bottomLeftEntries.length || bottomRightEntries.length ? [] : groundEntries;
  const hubSourceHandleSpecs: DiagramHandleSpec[] = [
    ...rightEntries.map((entry, index) => ({
      id: `right-${entry.index}`,
      position: 'right' as const,
      offsetPercent: spreadHandleOffset(rightEntries.length + directPayloadEntries.length, index, 7, 93),
    })),
    ...directPayloadEntries.map((entry, index) => ({
      id: `payload-${entry.index}`,
      position: 'right' as const,
      offsetPercent: spreadHandleOffset(
        rightEntries.length + directPayloadEntries.length,
        rightEntries.length + index,
        7,
        93,
      ),
    })),
    ...bottomEntries.map((entry, index) => ({
      id: `bottom-${entry.index}`,
      position: 'bottom' as const,
      offsetPercent: spreadHandleOffset(bottomEntries.length + directGroundEntries.length, index, 32, 68),
    })),
    ...bottomLeftEntries.map((entry) => ({
      id: `bottom-left-${entry.index}`,
      position: 'bottom' as const,
      offsetPercent: 32,
    })),
    ...bottomRightEntries.map((entry) => ({
      id: `bottom-right-${entry.index}`,
      position: 'bottom' as const,
      offsetPercent: 68,
    })),
    ...directGroundEntries.map((entry, index) => ({
      id: `ground-${entry.index}`,
      position: 'bottom' as const,
      offsetPercent: spreadHandleOffset(
        bottomEntries.length + directGroundEntries.length,
        bottomEntries.length + index,
        32,
        68,
      ),
    })),
  ];
  const hubTargetHandleSpecs: DiagramHandleSpec[] = leftEntries.map((entry, index) => ({
    id: `left-${entry.index}`,
    position: 'left',
    offsetPercent: spreadHandleOffset(leftEntries.length, index, 7, 93),
  }));

  if (hubEntry) {
    nodes.push(makeSubsystemNode(
      hubEntry,
      mode,
      { x: TOPOLOGY_HUB_X, y: TOPOLOGY_HUB_Y },
      { sourceHandleSpecs: hubSourceHandleSpecs, targetHandleSpecs: hubTargetHandleSpecs },
      selectedFailureNodeId,
    ));
    nodes[nodes.length - 1].data.hubNode = true;
  } else {
    nodes.push({
      id: hubId,
      type: 'subsystemDiagram',
      position: { x: TOPOLOGY_HUB_X, y: TOPOLOGY_HUB_Y },
      data: {
        label: 'On-Board Controller',
        status: 'root',
        badge: 'HUB',
        rootNode: true,
        hubNode: true,
        sourceHandleSpecs: hubSourceHandleSpecs,
        targetHandleSpecs: hubTargetHandleSpecs,
      },
    });
  }

  const getEntryNodeHeight = (entry: TopologyEntry): number =>
    getUniqueFailedTestCases(entry.subsystem.failedTestCases)?.length ? SUBSYSTEM_NODE_FAILURE_HEIGHT : SUBSYSTEM_NODE_HEIGHT;

  const leftRoutingIndices = computeRoutingIndices(
    leftEntries.length,
    (i) => TOPOLOGY_HUB_Y + (spreadHandleOffset(leftEntries.length, i, 7, 93) / 100) * HUB_NODE_HEIGHT,
    (i) => TOPOLOGY_LEFT_START_Y + i * TOPOLOGY_ROW_GAP + getEntryNodeHeight(leftEntries[i]) / 2,
  );

  const totalRightAndPayload = rightEntries.length + directPayloadEntries.length;
  const rightRoutingIndices = computeRoutingIndices(
    rightEntries.length,
    (i) => TOPOLOGY_HUB_Y + (spreadHandleOffset(totalRightAndPayload, i, 7, 93) / 100) * HUB_NODE_HEIGHT,
    (i) => TOPOLOGY_RIGHT_START_Y + i * TOPOLOGY_ROW_GAP + getEntryNodeHeight(rightEntries[i]) / 2,
  );

  leftEntries.forEach((entry, index) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_LEFT_X, y: TOPOLOGY_LEFT_START_Y + index * TOPOLOGY_ROW_GAP },
      { sourceHandle: 'right' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      nodeId,
      'right',
      hubId,
      `left-${entry.index}`,
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(leftEntries.length, index, 96),
      'target',
      routeLaneDistance(leftEntries.length, leftRoutingIndices[index], 48, 168),
    );
  });

  rightEntries.forEach((entry, index) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    const isPayloadServer = entry.lane === 'right' && entry.order === 0;
    const payloadSourceHandleSpecs = isPayloadServer
      ? payloadEntries.map((payloadEntry, payloadIndex) => ({
          id: `payload-${payloadEntry.index}`,
          position: 'right' as const,
          offsetPercent: spreadHandleOffset(payloadEntries.length, payloadIndex, 28, 72),
        }))
      : undefined;

    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_RIGHT_X, y: TOPOLOGY_RIGHT_START_Y + index * TOPOLOGY_ROW_GAP },
      { sourceHandleSpecs: payloadSourceHandleSpecs, targetHandle: 'left' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      hubId,
      `right-${entry.index}`,
      nodeId,
      'left',
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(rightEntries.length, index, 76),
      'source',
      routeLaneDistance(rightEntries.length, rightRoutingIndices[index], 48, 136),
    );
  });

  const payloadParentId = payloadParentEntry ? `subsystem-${payloadParentEntry.index}` : hubId;
  payloadEntries.forEach((entry, index) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_PAYLOAD_X, y: TOPOLOGY_RIGHT_START_Y + index * TOPOLOGY_ROW_GAP },
      { targetHandle: 'left' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      payloadParentId,
      `payload-${entry.index}`,
      nodeId,
      'left',
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(payloadEntries.length, index, 24),
      'source',
      routeLaneDistance(payloadEntries.length, index, 38, 92),
    );
  });

  bottomEntries.forEach((entry, index) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    const groundSourceHandleSpecs = index === 0
      ? groundEntries.map((groundEntry, groundIndex) => ({
          id: `ground-${groundEntry.index}`,
          position: 'bottom' as const,
          offsetPercent: spreadHandleOffset(groundEntries.length, groundIndex, 42, 58),
        }))
      : undefined;

    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_HUB_X + index * SESSION_SUBSYSTEM_COLUMN_GAP, y: TOPOLOGY_BOTTOM_Y },
      { sourceHandleSpecs: groundSourceHandleSpecs, targetHandle: 'top' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      hubId,
      `bottom-${entry.index}`,
      nodeId,
      'top',
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(bottomEntries.length, index, 28),
      'source',
      routeLaneDistance(bottomEntries.length, index, 54, 108),
    );
  });

  bottomLeftEntries.forEach((entry) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_LEFT_X + 210, y: TOPOLOGY_BOTTOM_Y },
      { targetHandle: 'top' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      hubId,
      `bottom-left-${entry.index}`,
      nodeId,
      'top',
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(bottomLeftEntries.length, 0, 28),
      'source',
      routeLaneDistance(1, 0, 54, 108),
    );
  });

  bottomRightEntries.forEach((entry) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_RIGHT_X - 360, y: TOPOLOGY_BOTTOM_Y },
      { targetHandle: 'top' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      hubId,
      `bottom-right-${entry.index}`,
      nodeId,
      'top',
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(bottomRightEntries.length, 0, 28),
      'source',
      routeLaneDistance(1, 0, 54, 108),
    );
  });

  const groundParentId = bottomEntries.length || bottomLeftEntries.length || bottomRightEntries.length ? `subsystem-${(bottomLeftEntries[0] || bottomRightEntries[0] || bottomEntries[0])?.index}` : hubId;
  groundEntries.forEach((entry, index) => {
    const nodeId = `subsystem-${entry.index}`;
    const status = getTopologyStatus(mode, entry.subsystem);
    nodes.push(makeSubsystemNode(
      entry,
      mode,
      { x: TOPOLOGY_HUB_X + index * SESSION_SUBSYSTEM_COLUMN_GAP, y: TOPOLOGY_GROUND_Y },
      { targetHandle: 'top' },
      selectedFailureNodeId,
    ));
    addTopologyEdge(
      edges,
      groundParentId,
      `ground-${entry.index}`,
      nodeId,
      'top',
      status,
      mode,
      selectedFailureNodeId,
      spreadRouteOffset(groundEntries.length, index, 28),
      'source',
      routeLaneDistance(groundEntries.length, index, 54, 108),
    );
  });

  return { nodes, edges };
}

function buildDiagram(
  rootLabel: string,
  mode: SubsystemDiagramMode,
  subsystems: SubsystemDiagramItem[],
  setupLabel?: string,
  selectedFailureNodeId?: string | null,
) {
  const subsystemCount = Math.max(subsystems.length, 1);
  const diagramColumns = Math.max(1, Math.min(3, subsystemCount));
  const nodes: DiagramFlowNode[] = [];
  const edges: DiagramFlowEdge[] = [];
  const rootNodeId = mode === 'session' ? 'session' : 'root';

  if (subsystems.length > 0) {
    return buildTopologyDiagram(mode, subsystems, selectedFailureNodeId);
  }

  if (mode === 'session') {
    nodes.push(
      {
        id: 'setup',
        type: 'subsystemDiagram',
        position: { x: SESSION_TREE_CENTER_X, y: SESSION_SETUP_Y },
        data: {
          label: setupLabel || 'Setup',
          status: 'root',
          badge: 'SETUP',
          rootNode: true,
          sourceHandle: 'bottom',
        },
      },
      {
        id: 'session',
        type: 'subsystemDiagram',
        position: { x: SESSION_TREE_CENTER_X, y: SESSION_NODE_Y },
        data: {
          label: rootLabel || 'Session',
          status: 'root',
          badge: 'SESSION',
          rootNode: true,
          sourceHandle: 'bottom',
          targetHandle: 'top',
        },
      },
    );

    edges.push({
      id: 'setup-session',
      source: 'setup',
      sourceHandle: 'bottom',
      target: 'session',
      targetHandle: 'top',
      type: 'straight',
      className: 'sdm__edge sdm__edge--session',
    });
  } else {
    nodes.push({
      id: 'root',
      type: 'subsystemDiagram',
      position: { x: SESSION_TREE_CENTER_X, y: SETUP_MODE_ROOT_Y },
      data: {
        label: rootLabel || 'Setup',
        status: 'root',
        badge: 'SETUP',
        rootNode: true,
        sourceHandle: 'bottom',
      },
    });
  }

  subsystems.forEach((subsystem, index) => {
    const nodeId = `subsystem-${index}`;
    const isSelectedFailure = nodeId === selectedFailureNodeId;
    const status = mode === 'setup' ? 'unknown' : normalizeStatus(subsystem.healthStatus);
    const failedTestCases = getUniqueFailedTestCases(subsystem.failedTestCases);
    const row = Math.floor(index / diagramColumns);
    const col = index % diagramColumns;
    const itemsInRow = Math.min(diagramColumns, subsystemCount - row * diagramColumns);
    const rowStartX = SESSION_TREE_CENTER_X - ((itemsInRow - 1) * SESSION_SUBSYSTEM_COLUMN_GAP) / 2;

    nodes.push({
      id: nodeId,
      type: 'subsystemDiagram',
      position: {
        x: rowStartX + col * SESSION_SUBSYSTEM_COLUMN_GAP,
        y: mode === 'session'
          ? SESSION_SUBSYSTEM_Y + row * SESSION_SUBSYSTEM_ROW_GAP
          : SETUP_MODE_SUBSYSTEM_Y + row * SESSION_SUBSYSTEM_ROW_GAP,
      },
      data: {
        label: subsystem.name,
        status,
        badge: statusBadge(status, mode),
        failedTestCases,
        selectedFailure: isSelectedFailure,
        targetHandle: 'top',
        staticNode: mode === 'setup',
      },
    });

    edges.push({
      id: `${rootNodeId}-subsystem-${index}`,
      source: rootNodeId,
      sourceHandle: 'bottom',
      target: nodeId,
      targetHandle: 'top',
      type: 'simplebezier',
      className: [
        'sdm__edge',
        mode === 'session' ? 'sdm__edge--health-link' : '',
        mode === 'setup' ? 'sdm__edge--setup-link' : '',
        mode === 'session' ? `sdm__edge--${status}` : '',
        isSelectedFailure ? 'sdm__edge--selected' : '',
      ].filter(Boolean).join(' '),
    });
  });

  return { nodes, edges };
}

export default function SubsystemDiagramModal({
  open,
  title,
  rootLabel,
  setupLabel,
  mode,
  subsystems,
  loading = false,
  sanityExecutionStatus,
  sanityTestPlanId,
  updatedAt,
  runSanityLoading = false,
  onRunSanityTest,
  isSessionCompleted = false,
  onClose,
}: SubsystemDiagramModalProps) {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const flowInstanceRef = useRef<ReactFlowInstance<DiagramFlowNode, DiagramFlowEdge> | null>(null);
  const nodeTypes = useMemo(() => ({ subsystemDiagram: DiagramNode }), []);
  const edgeTypes = useMemo(() => ({ topology: TopologyEdge }), []);
  const [selectedFailureNodeId, setSelectedFailureNodeId] = useState<string | null>(null);
  const [drawerFailureNodeId, setDrawerFailureNodeId] = useState<string | null>(null);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);
  const { nodes, edges } = useMemo(
    () => buildDiagram(rootLabel, mode, subsystems, setupLabel, selectedFailureNodeId),
    [rootLabel, mode, subsystems, setupLabel, selectedFailureNodeId],
  );
  const drawerFailureDetail = useMemo(
    () => {
      if (!drawerFailureNodeId) return null;

      const drawerNode = nodes.find((node) => node.id === drawerFailureNodeId);
      const testCases = getUniqueFailedTestCases(drawerNode?.data.failedTestCases);
      if (!drawerNode || !testCases?.length) return null;

      return {
        nodeId: drawerNode.id,
        subsystemName: drawerNode.data.label,
        testCases,
      };
    },
    [drawerFailureNodeId, nodes],
  );
  const drawerCopy = useMemo(
    () => (drawerFailureDetail ? failedTestCaseDetailCopy(drawerFailureDetail.testCases.length) : null),
    [drawerFailureDetail],
  );
  const showFailureDrawer = Boolean(mode === 'session' && drawerFailureDetail);
  const summary = useMemo(() => buildSummary(mode, subsystems), [mode, subsystems]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const canvasClassName = `sdm__canvas${showFailureDrawer ? ' sdm__canvas--drawer-visible' : ''}`;
  const setupChipLabel = setupLabel || rootLabel || 'Setup';
  const showRunSanityAction = mode === 'session' && !isSessionCompleted && Boolean(onRunSanityTest) && Boolean(sanityTestPlanId);
  const runSanityDisabled = runSanityLoading || loading || !sanityTestPlanId;
  const headerUpdatedCopy = isSessionCompleted ? '' : updatedAtCopy(updatedAt, nowMs);
  const viewportSettings = useMemo(() => {
    const settings = getViewportSettings(subsystems.length);
    if (!showFailureDrawer) return settings;

    return {
      ...settings,
      minZoom: Math.min(settings.minZoom, 0.24),
      maxZoom: Math.min(settings.maxZoom, 0.74),
    };
  }, [showFailureDrawer, subsystems.length]);

  useEffect(() => {
    if (!open) return;
    setSelectedFailureNodeId(null);
    setDrawerFailureNodeId(null);
    setIsDrawerClosing(false);
  }, [open]);

  useEffect(() => {
    if (!open || !updatedAt) return undefined;
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(intervalId);
  }, [open, updatedAt]);

  useEffect(() => {
    if (!open || !drawerFailureNodeId || drawerFailureDetail) return;
    setSelectedFailureNodeId(null);
    setDrawerFailureNodeId(null);
    setIsDrawerClosing(false);
  }, [drawerFailureDetail, drawerFailureNodeId, open]);

  useEffect(() => {
    if (!open || subsystems.length === 0) return undefined;

    const fitTimers = [
      window.setTimeout(() => {
        fitDiagramViewport(
          flowInstanceRef.current,
          canvasRef.current,
          nodes,
          viewportSettings,
          showFailureDrawer,
          false,
        );
      }, 80),
      window.setTimeout(() => {
        fitDiagramViewport(
          flowInstanceRef.current,
          canvasRef.current,
          nodes,
          viewportSettings,
          showFailureDrawer,
          false,
        );
      }, 260),
    ];

    return () => fitTimers.forEach(window.clearTimeout);
  }, [mode, nodes, open, showFailureDrawer, subsystems.length, viewportSettings]);

  function closeFailureDrawer() {
    if (!drawerFailureNodeId) return;
    setIsDrawerClosing(true);
  }

  function completeFailureDrawerClose(event: AnimationEvent<HTMLElement>) {
    if (!isDrawerClosing || event.currentTarget !== event.target) return;
    setDrawerFailureNodeId(null);
    setSelectedFailureNodeId(null);
    setIsDrawerClosing(false);
  }

  function toggleFailureDrawer(node: { id: string; data: DiagramNodeData }) {
    const currentNode = nodes.find((diagramNode) => diagramNode.id === node.id);
    const nodeData = currentNode?.data ?? node.data;
    if (!nodeData.failedTestCases?.length) return;

    if (drawerFailureNodeId === node.id && !isDrawerClosing) {
      closeFailureDrawer();
      return;
    }

    setSelectedFailureNodeId(node.id);
    setDrawerFailureNodeId(node.id);
    setIsDrawerClosing(false);
  }

  function runSanityTest() {
    if (!sanityTestPlanId || runSanityDisabled) return;
    void onRunSanityTest?.(sanityTestPlanId);
  }

  if (!open) return null;

  return (
    <div className="sdm__overlay" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="sdm__panel" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sdm__header">
          <div className="sdm__header__main">
            <div className="sdm__title-group">
              <span className="sdm__title">{title}</span>
            </div>
            {mode === 'session' && (
              <span className={`sdm__status-pill sdm__status-pill--${sanityStatusTone(sanityExecutionStatus)}`}>
                {sanityStatusCopy(sanityExecutionStatus)}
              </span>
            )}
            {headerUpdatedCopy && (
              <span className="sdm__updated-at">{headerUpdatedCopy}</span>
            )}
          </div>
          <div className="sdm__header__actions">
            {showRunSanityAction && (
              <button
                className={`sdm__run-sanity${runSanityLoading ? ' sdm__run-sanity--loading' : ''}`}
                type="button"
                disabled={runSanityDisabled}
                aria-busy={runSanityLoading}
                onClick={runSanityTest}
              >
                {runSanityLoading ? 'Running...' : 'Run Sanity Test'}
              </button>
            )}
            <button className="sdm__close" type="button" aria-label="Close subsystem diagram" onClick={onClose}>
              <span />
              <span />
            </button>
          </div>
        </div>

        <div ref={canvasRef} className={canvasClassName}>
          <div className="sdm__setup-chip" title={setupChipLabel}>
            <SetupChipIcon />
            <span>{setupChipLabel}</span>
          </div>
          <div className="sdm__legend" aria-label="Subsystem status summary">
            <span className="sdm__legend__item">Total {subsystems.length}</span>
            {mode === 'session' && (
              <>
                <span className="sdm__legend__item sdm__legend__item--healthy">Healthy {summary.healthy}</span>
                <span className="sdm__legend__item sdm__legend__item--unhealthy">Unhealthy {summary.unhealthy}</span>
                <span className="sdm__legend__item sdm__legend__item--pending">Pending {summary.pending}</span>
                {summary.unknown > 0 && (
                  <span className="sdm__legend__item sdm__legend__item--unknown">Unknown {summary.unknown}</span>
                )}
              </>
            )}
          </div>
          {loading ? (
            <div className="sdm__loader" role="status" aria-live="polite">
              <span className="sdm__loader__ring" />
              <span className="sdm__loader__title">Loading diagram</span>
              <span className="sdm__loader__text">Fetching subsystem data…</span>
            </div>
          ) : subsystems.length === 0 ? (
            <div className="sdm__empty">
              <span className="sdm__empty__title">No subsystem data</span>
              <span className="sdm__empty__text">Subsystem connectivity has not been configured for this setup yet.</span>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              colorMode="dark"
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              minZoom={viewportSettings.minZoom}
              maxZoom={viewportSettings.maxZoom}
              defaultViewport={{ x: 0, y: 0, zoom: viewportSettings.maxZoom }}
              fitView
              fitViewOptions={{
                padding: viewportSettings.padding,
                minZoom: viewportSettings.minZoom,
                maxZoom: viewportSettings.maxZoom,
                duration: 180,
              }}
              onNodeClick={(_event, node) => toggleFailureDrawer(node as { id: string; data: DiagramNodeData })}
              onInit={(instance) => {
                flowInstanceRef.current = instance;
              }}
              preventScrolling
              zoomOnScroll={false}
              zoomOnPinch={false}
              zoomOnDoubleClick={false}
              panOnScroll={false}
            >
              <Background
                color="rgba(240, 240, 240, 0.16)"
                gap={22}
                size={1.15}
                variant={BackgroundVariant.Dots}
              />
            </ReactFlow>
          )}
        </div>
        {showFailureDrawer && drawerFailureDetail && drawerCopy && (
          <aside
            key={drawerFailureDetail.nodeId}
            className={`sdm__failure-drawer${isDrawerClosing ? ' sdm__failure-drawer--closing' : ''}`}
            aria-label={drawerCopy.drawerLabel}
            onAnimationEnd={completeFailureDrawerClose}
          >
            <button
              className="sdm__failure-drawer__close"
              type="button"
              aria-label={drawerCopy.closeLabel}
              onClick={closeFailureDrawer}
            >
              <span />
            </button>
            <div className="sdm__failure-drawer__header">
              <span className="sdm__failure-drawer__eyebrow">Subsystem Health Detail</span>
              <div className="sdm__failure-drawer__identity">
                <span className="sdm__failure-drawer__icon">
                  {getSubsystemIcon(drawerFailureDetail.subsystemName)}
                </span>
                <span className="sdm__failure-drawer__title" title={drawerFailureDetail.subsystemName}>
                  {drawerFailureDetail.subsystemName}
                </span>
                <span className="sdm__failure-drawer__status">Unhealthy</span>
              </div>
            </div>
            <div className="sdm__failure-drawer__section">
              <span className="sdm__failure-drawer__section-title">{drawerCopy.sectionTitle}</span>
              <div className="sdm__failure-drawer__cases">
                {drawerFailureDetail.testCases.map((testCase) => (
                  <span className="sdm__failure-drawer__case" key={`${drawerFailureDetail.nodeId}-${testCase}`} title={formatTestCaseName(testCase)}>
                    <span className="sdm__failure-drawer__case-value">{formatReadableTestCaseName(testCase)}</span>
                  </span>
                ))}
              </div>
            </div>
            <span className="sdm__failure-drawer__hint">
              {drawerCopy.hint}
            </span>
          </aside>
        )}
      </div>
    </div>
  );
}
